<script setup lang="ts">
import { useExtensionPopupStore } from '~/stores/extensionPopup'

const emit = defineEmits<{
  (e: 'close'): void
}>()

const extensionPopupStore = useExtensionPopupStore()

const CHROME_EXTENSION_URL =
  'https://chrome.google.com/webstore/detail/dray-dog/emapbfnfnfnmgdhbnhobkodmfmdegbii'

const EDGE_EXTENSION_URL =
  'https://microsoftedge.microsoft.com/addons/detail/dray-dog/amjnmeggpfkiopblhfnhadjpelheppkd'

const CHROME_EXTENSION_VIDEO_URL = 'https://youtube.com/embed/LPw2W_RB9ic'

function getExtensionVideoUrl(): string | undefined {
  const ua = window.navigator.userAgent
  if (/Edg\//.test(ua)) {
    return CHROME_EXTENSION_VIDEO_URL // Using the same URL for now
  }
  if (/Chrome\//.test(ua)) {
    return CHROME_EXTENSION_VIDEO_URL
  }
  return undefined
}

function getExtensionStoreUrl(): string | null {
  const ua = window.navigator.userAgent
  if (/Edg\//.test(ua)) {
    return EDGE_EXTENSION_URL
  }
  if (/Chrome\//.test(ua)) {
    return CHROME_EXTENSION_URL
  }
  return null
}

function close() {
  emit('close')
}

async function install() {
  const url = getExtensionStoreUrl()
  if (url) {
    window.open(url, '_blank')
  }
  close()
}

function neverShow() {
  extensionPopupStore.neverShowAgain()
  close()
}

function showLater() {
  extensionPopupStore.remindLater()
  close()
}
</script>

<template>
  <transition name="fade">
    <el-dialog
      :model-value="true"
      align-center
      :close-on-click-modal="true"
      :show-close="false"
      :destroy-on-close="true"
      width="max-w-md"
      overlay-class="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-50"
      class="relative z-10 bg-white rounded-2xl shadow-lg max-w-md w-full p-5"
      @closed="close"
    >
      <button
        aria-label="Close"
        class="absolute top-3 right-3 text-gray-400 hover:text-gray-600 focus:outline-none"
        @click="close"
      >
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <h2 class="text-lg font-semibold text-gray-800 mb-2">
        Install browser extension?
      </h2>
      <p class="text-gray-600 mb-4 text-sm">
        The Dray Dog browser extension automatically captures appointments
        booked directly on emodal.com and termpoint.com and sends them to
        draydog.com so they appear <strong>immediately</strong> on Dray Dog
        <br />
        <br />
        If enabled, Dray Dog can send these appointments to your TMS as well!
      </p>
      <div v-if="getExtensionVideoUrl()" class="mb-4">
        <div class="w-full rounded-lg overflow-hidden" style="height: 250px">
          <iframe
            :src="getExtensionVideoUrl()"
            title="Dray Dog Extension Overview"
            allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture;
            "
            allowfullscreen
            class="w-full h-full"
          />
        </div>
      </div>
      <template #footer>
        <button
          class="w-full py-2 font-medium rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors mb-2"
          @click="install"
        >
          <i-mdi:download class="mr-2 text-lg align-text-bottom" />
          Install Extension
        </button>
        <button
          v-tooltip="'Remind me again in 24 hours.'"
          class="w-full py-2 font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 transition-colors mb-2 flex items-center justify-center"
          @click="showLater"
        >
          <i-mdi:clock-outline class="mr-2 text-base align-text-bottom" />
          Remind me later
        </button>
        <button
          v-tooltip="'You will not see this popup again.'"
          class="w-full py-2 font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 transition-colors flex items-center justify-center"
          @click="neverShow"
        >
          <i-mdi:eye-off-outline class="mr-2 text-base align-text-bottom" />
          Never show again
        </button>
      </template>
    </el-dialog>
  </transition>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
